import { Card, CompartmentCard } from "@/src/components/cards";
import { openConfirmDialog, openDrawer } from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import {
  FolderItem,
  Patient,
  PatientDocument,
  PatientDocumentLight,
  Sample,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getUserShortName, uid } from "@/src/utils/utils";
import moment from "moment";
import React from "react";
import { DocumentTemplateChoiceList } from "../documents/documentTemplates";
import { openDocumentForm, openEditor } from "../documents/documentsHook";
import { HtmlDiv } from "../documents/htmlDiv";
import { Button } from "@/src/components/buttons";

export default function AnapathDocumentsCompartment({
  patient,
  parent,
  onCreate,
  onValidate,
}: {
  patient: Patient;
  parent: Sample;
  onCreate: (patientDocument: PatientDocument) => void;
  onValidate: (patientDocument: PatientDocument) => void;
}) {
  const [reload, setReload] = React.useState(0);

  return (
    <div className="c1 gap10">
      <FetchableList
        queryKey={[
          "patientDocuments",
          getItemId(patient),
          getItemId(parent),
          reload,
        ]}
        queryFn={apiFetch(
          "patientDocuments",
          "loadMany",
          {
            patientId: getItemId(patient),
            parentId: getItemId(parent),
          },
          "Erreur lors du chargement des documents"
        )}
      >
        {(patientDocuments) => (
          <div className="c1 gap10">
            {patientDocuments?.map((patientDocument, index) => {
              return (
                <AnapathDocumentCard
                  key={uid()}
                  patientDocument={patientDocument}
                  onEdit={async () => {
                    let fullPatientDocument = await apiFetch(
                      "patientDocuments",
                      "loadOne",
                      {
                        id: patientDocument?.id,
                      },
                      "Erreur lors du chargement du document"
                    );

                    if (!fullPatientDocument)
                      throw new Error("Le Document n'existe pas.");

                    console.log("template", fullPatientDocument.template);
                    openDocumentForm({
                      patient,
                      patientDocument: fullPatientDocument,
                      onChange: () => {
                        setReload(reload + 1);
                      },
                    });
                  }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    let fullPatientDocument = await apiFetch(
                      "patientDocuments",
                      "loadOne",
                      {
                        id: patientDocument?.id,
                      },
                      "Erreur lors du chargement du document"
                    );
                    if (!fullPatientDocument)
                      throw new Error("Le Document n'existe pas.");

                    if (!fullPatientDocument.template) {
                      fullPatientDocument.template = {
                        title: fullPatientDocument.title || "",
                        content: fullPatientDocument.content,
                        format: "a4",
                        isValidatable: false,
                        creator: USER?.get(),
                        name: "",
                        showHeader: true,
                        showLeftBar: true,
                        barcodeLocation: "none",
                        showPatientIdentity: true,
                        showTitle: true,
                        parent: null,
                      };
                    }

                    openEditor({
                      patient,
                      patientDocument: fullPatientDocument,
                      onChange: () => {
                        setReload(reload + 1);
                      },
                      onSave: (patientDocument) => {
                        onCreate(patientDocument);
                      },
                      onValidate: (patientDocument) => {
                        onValidate(patientDocument);
                      },
                    });
                  }}
                  onDelete={async () => {
                    await openConfirmDialog({
                      id: "deleteDocument",
                      title: "Supprimer le document",
                      content: "Voulez-vous vraiment supprimer ce document?",
                      onConfirm: async () => {
                        await apiFetch(
                          "patientDocuments",
                          "deleteOne",
                          {
                            id: patientDocument.id,
                          },
                          "Erreur lors de la suppression du document"
                        );
                        setReload(reload + 1);
                      },
                    });
                  }}
                ></AnapathDocumentCard>
              );
            })}
          </div>
        )}
      </FetchableList>
      {/* <Button
        type="tertiary"
        onClick={async () => {
          await choseDocumentTemplate({
            patient,
            parent,
            onDocumentCreate: (patientDocument) => {
              onCreate(patientDocument);
              setReload(reload + 1);
            },
            onDocumentValidate: (patientDocument) => {
              onValidate(patientDocument);
              setReload(reload + 1);
            },
          });
        }}
      >
        Nouveau document
      </Button> */}
    </div>
  );
}

export async function choseAnapathDocumentTemplate({
  patient,
  parent,
  onDocumentCreate,
  onDocumentValidate,
}: {
  patient: Patient;
  parent: Sample;
  onDocumentCreate: (patientDocument: PatientDocument) => void;
  onDocumentValidate: (patientDocument: PatientDocument) => void;
}) {
  await openDrawer({
    id: "documentTemplateChoiceDrawer",
    title: "Choisir un modèle de document",
    content: (
      <DocumentTemplateChoiceList
        defaultTemplates={(
          USER.get()?.speciality?.frequentTemplates || []
        ).filter((template) => template != null)}
        types={["standard", "letter"]}
        onSelect={async (template) => {
          let patientDocument: PatientDocument = {
            patient: patient,
            creator: USER?.get(),

            template: template,
            parent,
            content: template.content,
            title: template.title || "Nouveau document",
            status: "pending",
            refAnswer: {
              creator: USER?.get(),
              reference: template.reference,
              patient: patient,
              fieldAnswers: [],
              notes: "",
              conclusion: "",
            },
          };

          await openDocumentForm({
            patient,
            patientDocument,
            onSave: (patientDocument) => {
              onDocumentCreate?.(patientDocument);
            },
            onValidate: (patientDocument) => {
              onDocumentValidate?.(patientDocument);
            },
          });
        }}
      />
    ),
  });
}

function AnapathDocumentCard({
  patientDocument,
  onEdit,
  onClick,
  onDelete,
}: {
  patientDocument: PatientDocumentLight;
  onEdit: (e: any) => void;
  onClick: (e: any) => void;
  onDelete: (e: any) => void;
}) {
  return (
    <Card
      color={"green"}
      className={patientDocument.status == "pending" ? "empty" : ""}
      title={
        <HtmlDiv>
          {patientDocument.title || patientDocument.template?.title || ""}
        </HtmlDiv>
      }
      trailing={
        <div className="c1 gap10">
          {patientDocument.status == "pending" ? (
            <div className="grayish br p10">Non validé</div>
          ) : (
            <div className=" greenish br p10">Validé</div>
          )}
        </div>
      }
      subTitle={
        <div>
          Rédigé par {getUserShortName(patientDocument.creator)}
          {patientDocument.isValidatedBy && (
            <div className="bold black">
              Validé par {getUserShortName(patientDocument.isValidatedBy)}
            </div>
          )}
        </div>
      }
      onClick={onClick}
      onEdit={onEdit}
      onDelete={onDelete}
    >
      <div className="gray">
        {moment(patientDocument._date).format("LL HH:mm ")}
      </div>
    </Card>
  );
}
